/**
 * HighlightAI · Publishing — Coming Soon
 * -------------------------------------------------
 * Frontend-only sprint. Publication réelle (TikTok, YouTube, Instagram,
 * LinkedIn, X) n'est pas encore branchée : cette page l'annonce clairement,
 * sans aucun bouton trompeur, sans aucun appel API, sans faux état
 * "connecté". Le backend OAuth reste intact — cette page est simplement
 * son remplacement visuel tant que l'upload média final n'est pas livré.
 *
 * Style vocabulary : Linear / Raycast / Vercel — dark canvas, hairline
 * borders (white/6-10 %), lime accent (#D4FF00), Framer Motion très
 * légères (opacity + petits translates).
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Construction, CheckCircle2, Circle, ArrowRight, Shield,
  Youtube, Instagram, Linkedin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Brand marks not available in Lucide are provided as compact inline SVGs.
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M19.6 6.9a5.6 5.6 0 0 1-3.3-1.1 5.6 5.6 0 0 1-2.2-3.6h-3v13.3a2.6 2.6 0 1 1-2.6-2.6c.3 0 .5 0 .8.1v-3.1a5.7 5.7 0 1 0 4.9 5.6V9.5a8.7 8.7 0 0 0 5.4 1.9V8.3l-.1-1.4Z" />
  </svg>
);
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.914l-5.417-6.79L4.5 22H1.244l8.03-9.17L1 2h7.086l4.89 6.238L18.244 2Zm-1.213 18h1.926L7.05 4H5.02l12.011 16Z" />
  </svg>
);

// ────────────────────────────────────────────────────────────── data

const PLATFORMS = [
  { id: 'tiktok',    name: 'TikTok',           Icon: TikTokIcon },
  { id: 'youtube',   name: 'YouTube Shorts',   Icon: Youtube },
  { id: 'instagram', name: 'Instagram Reels',  Icon: Instagram },
  { id: 'linkedin',  name: 'LinkedIn',         Icon: Linkedin },
  { id: 'x',         name: 'X',                Icon: XIcon },
];

const ROADMAP = [
  { id: 'oauth',     label: 'OAuth sécurisé développé',   done: true  },
  { id: 'youtube',   label: 'Publication YouTube',        done: false },
  { id: 'linkedin',  label: 'Publication LinkedIn',       done: false },
  { id: 'tiktok',    label: 'Publication TikTok',         done: false },
  { id: 'instagram', label: 'Publication Instagram',      done: false },
  { id: 'x',         label: 'Publication X',              done: false },
];

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

// ────────────────────────────────────────────────────────────── atoms

const SectionLabel = ({ children }) => (
  <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{children}</span>
);

const PlatformCard = ({ p, idx }) => {
  const Icon = p.Icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.05 + idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="group rounded-2xl border border-white/5 bg-white/[0.02] p-5 flex flex-col gap-4 hover:border-white/10 transition-colors"
      data-testid={`publishing-platform-${p.id}`}
    >
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/5 flex items-center justify-center text-zinc-300">
          <Icon className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-100 truncate">{p.name}</div>
          <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-600" />
            Bientôt disponible
          </div>
        </div>
      </div>

      <button
        type="button"
        disabled
        aria-disabled="true"
        className="w-full h-9 rounded-lg border border-white/5 bg-white/[0.02] text-xs font-medium text-zinc-500 cursor-not-allowed select-none"
        data-testid={`publishing-cta-${p.id}`}
      >
        Arrive prochainement
      </button>
    </motion.div>
  );
};

const RoadmapItem = ({ item, idx }) => {
  const Icon = item.done ? CheckCircle2 : Circle;
  return (
    <motion.li
      initial={{ opacity: 0, x: -6 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay: idx * 0.04 }}
      className="flex items-center gap-3 py-2.5"
      data-testid={`publishing-roadmap-${item.id}`}
    >
      <Icon
        className={`w-4 h-4 shrink-0 ${item.done ? 'text-[#D4FF00]' : 'text-zinc-600'}`}
        strokeWidth={item.done ? 2.4 : 2}
      />
      <span className={`text-sm ${item.done ? 'text-zinc-200' : 'text-zinc-500'}`}>
        {item.label}
      </span>
      {item.done && (
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#D4FF00]/70">
          Livré
        </span>
      )}
    </motion.li>
  );
};

// ────────────────────────────────────────────────────────────── page

export default function Publishing() {
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="publishing-page">
      {/* ───── Hero ───── */}
      <motion.section {...fadeUp} className="text-center max-w-3xl mx-auto">
        <div
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5"
          data-testid="publishing-status-badge"
        >
          <Construction className="w-3.5 h-3.5 text-[#D4FF00]" strokeWidth={2.2} />
          <span className="text-[11px] uppercase tracking-widest text-zinc-300 font-mono">
            En développement
          </span>
        </div>

        <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white">
          Publication automatique
        </h1>
        <p className="mt-5 text-base sm:text-lg text-zinc-400 leading-relaxed">
          Connecte bientôt tes réseaux sociaux et publie automatiquement tes clips
          générés par HighlightAI.
        </p>
      </motion.section>

      {/* ───── Platforms grid ───── */}
      <section className="mt-16" data-testid="publishing-platforms-section">
        <div className="flex items-center justify-between mb-5">
          <SectionLabel>Plateformes</SectionLabel>
          <span className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono">
            {PLATFORMS.length} intégrations en préparation
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {PLATFORMS.map((p, i) => <PlatformCard key={p.id} p={p} idx={i} />)}
        </div>
      </section>

      {/* ───── Central card ───── */}
      <motion.section
        {...fadeUp}
        className="mt-16 relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-8 sm:p-12"
        data-testid="publishing-central-card"
      >
        <div
          aria-hidden="true"
          className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#D4FF00]/5 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-2 mb-5 rounded-md border border-[#D4FF00]/20 bg-[#D4FF00]/5 px-2.5 py-1">
            <Shield className="w-3.5 h-3.5 text-[#D4FF00]" strokeWidth={2.2} />
            <span className="text-[10px] uppercase tracking-widest text-[#D4FF00]/90 font-mono">
              OAuth 2.0 · AES-256-GCM
            </span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
            Publication automatique arrive bientôt
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-400 leading-relaxed">
            Nous finalisons actuellement l&apos;intégration officielle avec les API de
            TikTok, YouTube, Instagram, LinkedIn et X afin de proposer une publication
            fiable et sécurisée.
          </p>
          <p className="mt-3 text-sm sm:text-base text-zinc-400 leading-relaxed">
            Les créateurs pourront connecter leurs comptes et publier leurs clips en
            quelques clics directement depuis HighlightAI.
          </p>
        </div>
      </motion.section>

      {/* ───── Roadmap ───── */}
      <section className="mt-16" data-testid="publishing-roadmap-section">
        <SectionLabel>Roadmap</SectionLabel>
        <div className="mt-4 rounded-2xl border border-white/5 bg-white/[0.02] px-5 sm:px-6 py-3 divide-y divide-white/5">
          {ROADMAP.map((item, i) => <RoadmapItem key={item.id} item={item} idx={i} />)}
        </div>
      </section>

      {/* ───── CTA ───── */}
      <motion.section {...fadeUp} className="mt-16 text-center">
        <Link to="/dashboard">
          <Button
            className="bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold h-11 px-6 rounded-lg"
            data-testid="publishing-cta-dashboard"
          >
            Continuer à générer des clips
            <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2.4} />
          </Button>
        </Link>
        <p className="mt-4 text-xs text-zinc-600">
          En attendant, concentre-toi sur la production : la publication te sera
          proposée automatiquement dès qu&apos;elle sera disponible.
        </p>
      </motion.section>
    </div>
  );
}
